import { tenantPublicPortalExpectations } from '@customize/publicPortalRegistry';
import { isSsoEnabled } from './ssoSession';
import { parseUnifiedPortalPaths } from './unifiedPortalPaths';

export type PublicPortalProfile =
  | { mode: 'legacy_split' }
  | { mode: 'unified'; basePath: string }
  | { mode: 'invalid' };

/**
 * Local login needs no SSO setup when all SSO features are off. An explicit
 * registry or an enabled SSO feature still requires the tenant's unified
 * declaration to agree with the route registry.
 */
export const resolvePublicPortalProfile = (
  site: string | undefined,
  rawRegistry: unknown = import.meta.env.VITE_SSO_UNIFIED_PORTAL_PATHS,
): PublicPortalProfile => {
  if (!site || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(site)) return { mode: 'invalid' };

  const registryIsUnconfigured = rawRegistry === undefined ||
    (typeof rawRegistry === 'string' && rawRegistry.trim() === '');
  const hasEnabledSsoFeature = [
    import.meta.env.VITE_SSO_ENABLED,
    import.meta.env.VITE_SSO_PASSIVE_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
  ].some(isSsoEnabled);

  if (registryIsUnconfigured && !hasEnabledSsoFeature) {
    return { mode: 'legacy_split' };
  }

  const expectsUnified = tenantPublicPortalExpectations[site]?.unified === true;
  const basePath = parseUnifiedPortalPaths(rawRegistry)[site];

  if (!expectsUnified && basePath === undefined) return { mode: 'legacy_split' };
  if (!expectsUnified || basePath !== `/${site}`) return { mode: 'invalid' };

  return { mode: 'unified', basePath };
};
